import { inject, Injectable } from '@angular/core';
import { AddStorageAccountWizardComponent } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.component';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { I18NextService } from 'angular-i18next';
import { MbsSize, ModalService } from 'mbs-ui-kit';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { StorageType } from 'mbs-ui-shared@models';
import { StoragesAvailableForCreation } from 'mbs-ui-shared@models/StorageAccount';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { CreateBackupDestinationContext, CreateBackupDestinationService } from 'wizards/tokens/create-backup-destination.token';

const withoutAzureVM = (storages: StoragesAvailableForCreation): StoragesAvailableForCreation =>
  storages.filter(({ type }) => type !== StorageType.AzureVM);

@Injectable({ providedIn: 'root' })
export class CreateBackupDestinationWizardService implements CreateBackupDestinationService {
  private modalService = inject(ModalService);
  private storageAccountsService = inject(StorageAccountsService);
  private companiesFacade = inject(CompaniesFacade);
  private computersFacade = inject(ComputersFacade);
  private i18nextService = inject(I18NextService);

  public open({ hid }: CreateBackupDestinationContext): Observable<boolean> {
    // Assigns the destination to the computer's company (the MBS-31970 flow), so the computer's user actually gets it
    return this.computersFacade.getByHid$(hid, true).pipe(
      catchError(() => of(null)),
      switchMap((computer) => this.openWizard(computer?.company?.id ? [computer.company.id] : null, withoutAzureVM)),
      map((createdAccountId) => !!createdAccountId)
    );
  }

  public openForAllCompanies(): Observable<string | null> {
    return this.companiesFacade.getCompaniesCompact({ offset: 0, limit: null }).pipe(
      switchMap(({ data }) =>
        this.openWizard(
          (data ?? []).map(({ id }) => id),
          withoutAzureVM
        )
      )
    );
  }

  public openForCompany(companyId: string | null, allowedTypes: StorageType[]): Observable<boolean> {
    return this.openWizard(companyId ? [companyId] : null, (storages) => storages.filter(({ type }) => allowedTypes.includes(type))).pipe(
      map((createdAccountId) => !!createdAccountId)
    );
  }

  private openWizard(
    companyIds: string[] | null,
    filterStorages: (storages: StoragesAvailableForCreation) => StoragesAvailableForCreation
  ): Observable<string | null> {
    // The wizard's namespace is normally loaded by the Storages route resolver — absent in the wizard-step contexts
    return from(this.i18nextService.loadNamespaces(['storages'])).pipe(
      switchMap(() => this.storageAccountsService.getStoragesAvailableForStorageCreation()),
      switchMap((availableStorages) => {
        const wizard = this.modalService.openRef(AddStorageAccountWizardComponent, {
          size: MbsSize.lg,
          backdrop: 'static',
          keyboard: false
        });

        wizard.componentInstance.mode = CreationMode.Create;
        wizard.componentInstance.availableStorages = filterStorages(availableStorages);
        wizard.componentInstance.companyIds = companyIds;

        return from(wizard.result).pipe(
          map((res) => (res === 'success' ? (wizard.componentInstance.storageId as string) : null)),
          catchError(() => of(null))
        );
      })
    );
  }
}
